import * as React from 'react';

import tinytime from 'tinytime';

import { SkipLogicSVG } from '@components/svgs';
import { find } from '@components/utils';
import { slValueToArray } from 'components/Public/GQSurvey/skipLogic';

import { SKIP_LOGIC_OP } from '../constants';
import { calculateNumber } from '../utils';

import {
  END_OF_SURVEY_ALLOWING_AUTO_REVIEW,
  END_OF_SURVEY_PREVENTING_AUTO_REVIEW,
  END_OF_SURVEY_WITHOUT_SUBMIT
} from './SkipLogicSlideOut';

const dateTemplate = tinytime('{MM} {DD}, {YYYY}');

const EMPTY_QUESTION = '<deleted question>';
const EMPTY_SKIP_LOGIC_VALUE = '<none>';
const SKIP_LOGIC_FINAL_STEP = 'the final step';

const SKIP_LOGIC_FINAL_STEP_EXCLUDE_FROM_AUTO_REVIEW = (): React.ReactNode => (
  <span>
    the final step (and <span className='underline'>exclude</span> from automatic review)
  </span>
);

const SKIP_LOGIC_FINAL_STEP_ALLOW_AS_IDEAL = (): React.ReactNode => (
  <span>
    the final step (but <span className='underline'>include</span> in automatic review)
  </span>
);

const SKIP_LOGIC_FINAL_STEP_SKIP_SUBMIT = (): React.ReactNode => (
  <span>
    the final step (<span className='underline'>without</span> collecting the response)
  </span>
);

const UNTITLED_QUESTION = '(Untitled question)';

interface SkipLogicRendererProps {
  question: ScreenerField;
  questions: ScreenerField[];
  autoReview: boolean;
  hasAnIdealAnswer: boolean;
}

export const SkipLogicSummary: React.FC<SkipLogicRendererProps> = ({
  question,
  questions,
  autoReview,
  hasAnIdealAnswer
}) => {
  const getSkipLogicValueLabel = (skipLogic: SkipLogic): string => {
    if (!skipLogic.value) return '';

    if (question.field_type === 'date') {
      return dateTemplate.render(new Date(+skipLogic.value));
    }

    if (['single_choice', 'multiple_choice'].includes(question.field_type)) {
      return slValueToArray(skipLogic.value).join(', ');
    }

    if (['any_answer', 'no_answer'].includes(question.field_type)) {
      return EMPTY_SKIP_LOGIC_VALUE;
    }

    return skipLogic.value;
  };

  const getNextQuestionFieldName = (skipLogic: SkipLogic): string | React.ReactNode => {
    if (!skipLogic) return EMPTY_QUESTION;

    if (skipLogic.to === END_OF_SURVEY_PREVENTING_AUTO_REVIEW) {
      if (autoReview && !hasAnIdealAnswer) {
        return SKIP_LOGIC_FINAL_STEP_EXCLUDE_FROM_AUTO_REVIEW();
      } else {
        return SKIP_LOGIC_FINAL_STEP;
      }
    }

    if (skipLogic.to === END_OF_SURVEY_ALLOWING_AUTO_REVIEW) {
      return SKIP_LOGIC_FINAL_STEP;
    }

    if (skipLogic.to === END_OF_SURVEY_WITHOUT_SUBMIT) {
      return SKIP_LOGIC_FINAL_STEP_SKIP_SUBMIT();
    }

    const next = find(questions, (q) => q.id === skipLogic.to);
    const number = calculateNumber(
      questions,
      questions.findIndex((q) => q.id === next?.id)
    );

    return `#${number} ${next?.label ?? UNTITLED_QUESTION}`;
  };

  const { skip_logic } = question;

  if (skip_logic) {
    return (
      <>
        <SkipLogicSVG className='flex-shrink-0 text-indigo-600' />
        <div className='text-sm'>
          {skip_logic.map((skipLogic, i) => {
            if (!skipLogic?.to) return null;

            const operator = SKIP_LOGIC_OP[skipLogic.op];
            const value = getSkipLogicValueLabel(skipLogic);
            const next = getNextQuestionFieldName(skipLogic);

            return (
              <span key={i}>
                If response {operator} <span className='font-bold'>{value}</span> skip to{' '}
                <span className='font-bold'>{next}</span>{' '}
              </span>
            );
          })}
        </div>
      </>
    );
  }

  return null;
};
